import type { PostProcessorModule } from 'i18next'
import posthog from 'posthog-js'
import { DEFAULT_LANGUAGE, PSEUDO_LOCALE } from './languages'

/**
 * Missing-key reporting. Runs as a post-processor with `postProcessPassResolved`, so it sees where
 * each string really came from. A key counts as missing in a language when the lookup found
 * nothing at all, or only found the English fallback. Production sends each one once per session
 * to PostHog as `i18n_missing_key`; development warns in the console instead.
 */

const SESSION_KEY = 'maayot.i18nMissing'
const reported = new Set<string>()

const alreadyReported = (id: string) => {
  if (reported.has(id)) return true
  reported.add(id)
  try {
    const stored: string[] = JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || '[]')
    if (stored.includes(id)) return true
    stored.push(id)
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(stored))
  } catch {
    /* storage blocked: the in-memory set still dedupes this page view */
  }
  return false
}

export interface MissingKey {
  key: string
  ns: string
  lng: string
}

export const reportMissingKey = (missing: MissingKey) => {
  if (alreadyReported(`${missing.lng}|${missing.ns}|${missing.key}`)) return
  if (process.env.NODE_ENV === 'production') {
    if ((posthog as any).__loaded) posthog.capture('i18n_missing_key', { ...missing })
  } else if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn(`[i18n] missing ${missing.lng} ${missing.ns}:${missing.key}`)
  }
}

/** Test hook: forget what was reported. */
export const resetMissingKeyReports = () => {
  reported.clear()
  try {
    window.sessionStorage.removeItem(SESSION_KEY)
  } catch {
    /* nothing to clear */
  }
}

export const missingKeyPostProcessor: PostProcessorModule = {
  type: 'postProcessor',
  name: 'reportMissing',
  process(value: any, key: string | string[], options: any, translator: any) {
    const resolved = options?.i18nResolved
    const language: string | undefined = options?.lng || translator?.language
    if (!resolved || !language || language === 'cimode') return value
    const ns: string =
      resolved.usedNS ||
      (Array.isArray(options?.ns) ? options.ns[0] : options?.ns) ||
      translator?.options?.defaultNS?.[0] ||
      translator?.options?.defaultNS
    // Strings asked for before their namespace has loaded are not missing, just early.
    if (!translator?.utils?.hasLoadedNamespace?.(ns)) return value
    const wanted = language === PSEUDO_LOCALE ? DEFAULT_LANGUAGE : language
    const foundIn = resolved.res !== undefined ? resolved.usedLng : null
    const foundInWanted = foundIn === wanted || (language === PSEUDO_LOCALE && foundIn === PSEUDO_LOCALE)
    if (!foundInWanted) {
      reportMissingKey({ key: String(resolved.usedKey || (Array.isArray(key) ? key[0] : key)), ns, lng: wanted })
    }
    return value
  },
}
